import { useEffect, useRef } from "react";

export default function IntonationHelpModal({ open, onClose }) {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const previouslyFocused = document.activeElement;

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusFrame = window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });

    return () => {
      window.cancelAnimationFrame(focusFrame);
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;

      if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus();
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  const intonationImageSrc =
    `${process.env.REACT_APP_USER_API_URL}/uploads/screens/muchwarmerthere.png`;
  const voicingImageSrc =
    `${process.env.REACT_APP_USER_API_URL}/uploads/screens/gutten_skriver.png`;

  const voicingPattern = [
    ["Gu", "mostly voiced", true],
    ["tt", "voiceless", false],
    ["en", "voiced", true],
    ["sk", "voiceless", false],
    ["-river", "mostly voiced", true],
  ];

  return (
    <div className="fixed inset-0 z-[250]">
      <button
        type="button"
        aria-label="Close help"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />

      <div className="absolute inset-0 p-0 sm:p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="intonation-help-title"
          aria-describedby="intonation-help-subtitle"
          className="relative flex h-full w-full flex-col overflow-hidden bg-white sm:rounded-2xl sm:border sm:border-gray-200 sm:shadow-2xl"
          onClick={(event) => event.stopPropagation()}
        >
          <header className="sticky top-0 z-20 shrink-0 border-b border-gray-200 bg-white">
            <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6">
              <div className="min-w-0">
                <h2
                  id="intonation-help-title"
                  className="truncate text-xl font-semibold text-gray-900 sm:text-2xl"
                >
                  How to read the graph
                </h2>
                <p
                  id="intonation-help-subtitle"
                  className="mt-0.5 truncate text-base text-gray-600 sm:text-lg"
                >
                  Pitch movement, sound intensity, and voiced and voiceless sounds
                </p>
              </div>

              <button
                ref={closeButtonRef}
                type="button"
                onClick={onClose}
                className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-base font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50"
              >
                <span className="text-lg leading-none" aria-hidden="true">
                  ✕
                </span>
                <span className="hidden sm:inline">Close</span>
              </button>
            </div>
          </header>

          <main className="min-h-0 flex-1 overflow-y-auto">
            <div className="mx-auto w-full max-w-6xl space-y-9 px-4 py-6 sm:px-6 lg:px-8">
              <section className="space-y-4" aria-labelledby="pitch-movement-heading">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="inline-flex rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-semibold text-blue-700">
                      English example
                    </div>

                    <h3
                      id="pitch-movement-heading"
                      className="text-xl font-semibold text-gray-900 sm:text-2xl"
                    >
                      1. Follow the overall pitch movement
                    </h3>
                  </div>

                  <p className="mt-2 max-w-5xl text-base leading-7 text-gray-700 sm:text-lg sm:leading-8">
                    The blue curve shows how pitch moves through the phrase. Focus
                    first on the broad rise-and-fall pattern rather than every small
                    fluctuation.
                  </p>
                </div>

                <figure className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
                  <img
                    src={intonationImageSrc}
                    alt="Pitch and intensity curves for the English question Wouldn't it be much warmer there?"
                    className="block h-auto w-full"
                  />
                  <figcaption className="border-t border-gray-100 bg-gray-50 p-4 text-base leading-7 text-gray-700 sm:p-5 sm:text-lg sm:leading-8">
                    In “Wouldn’t it be much warmer there?”, read the voiced parts as
                    one broad phrase-level contour rather than as unrelated curves
                    for individual words. Pitch can change inside a word, and brief
                    gaps do not necessarily mark word or phrase boundaries. In this
                    recording, the contour rises again toward the end—a common, but
                    not universal, pattern in English yes/no questions.
                  </figcaption>
                </figure>
              </section>

              <section className="space-y-4" aria-labelledby="pitch-gaps-heading">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="inline-flex rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-sm font-semibold text-indigo-700">
                      Norwegian example
                    </div>

                    <h3
                      id="pitch-gaps-heading"
                      className="text-xl font-semibold text-gray-900 sm:text-2xl"
                    >
                      2. Understand why the blue curve has gaps
                    </h3>
                  </div>

                  <p className="mt-2 max-w-5xl text-base leading-7 text-gray-700 sm:text-lg sm:leading-8">
                    The blue curve appears when vocal-fold vibration is regular
                    enough for the system to measure pitch. It normally disappears
                    during voiceless sounds and pauses. The grey intensity curve can
                    continue because sound energy may still be present.
                  </p>
                </div>

                <figure className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
                  <img
                    src={voicingImageSrc}
                    alt="Pitch and intensity curves for the Norwegian sentence Gutten skriver, with pitch gaps around tt and sk"
                    className="block h-auto w-full"
                  />
                </figure>

                <div className="rounded-2xl border border-blue-200 bg-blue-50/40 p-4 sm:p-5">
                  <div className="text-lg font-semibold text-gray-900 sm:text-xl">
                    Approximate voicing pattern
                  </div>

                  <div className="mt-3 grid grid-cols-5 overflow-hidden rounded-xl border border-gray-200 bg-white text-center">
                    {voicingPattern.map(([sound, label, voiced], index) => (
                      <div
                        key={sound}
                        className={index < 4 ? "border-r border-gray-200" : ""}
                      >
                        <div
                          className={[
                            "px-1.5 py-2.5 text-sm font-semibold sm:px-3 sm:text-base",
                            voiced ? "text-blue-700" : "text-slate-700",
                          ].join(" ")}
                        >
                          {sound}
                        </div>
                        <div
                          className={[
                            "border-t border-gray-200 px-1 py-2.5 text-xs font-medium leading-tight sm:px-2 sm:text-sm",
                            voiced
                              ? "bg-blue-50 text-blue-700"
                              : "bg-slate-50 text-slate-700",
                          ].join(" ")}
                        >
                          {label}
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="mt-4 text-base leading-7 text-gray-700 sm:text-lg sm:leading-8">
                    In the simplified pattern above, the blue pitch curve is mostly
                    present during <span className="font-semibold text-blue-700">Gu</span>,{" "}
                    <span className="font-semibold text-blue-700">en</span>, and{" "}
                    <span className="font-semibold text-blue-700">-river</span>. It
                    disappears around the voiceless{" "}
                    <span className="font-semibold text-slate-800">tt</span> and{" "}
                    <span className="font-semibold text-slate-800">sk</span>. The
                    grey curve shows sound level in dB and may remain visible during
                    both voiced and voiceless sounds.
                  </p>

                  <p className="mt-2 text-sm leading-6 text-gray-500 sm:text-base">
                    The boundaries are approximate because neighbouring sounds
                    overlap during natural articulation.
                  </p>

                  <div className="mt-4 rounded-xl border border-blue-200 bg-white px-4 py-3 text-base font-medium leading-7 text-gray-800 sm:text-lg sm:leading-8">
                    A blue gap does not mean that pitch physically falls to 0 Hz. It
                    means that no reliable pitch was detected at that moment.
                  </div>
                </div>
              </section>

              <section className="rounded-2xl border border-gray-200 bg-white p-5">
                <div className="text-xl font-semibold text-gray-900 sm:text-2xl">
                  3. When your gaps differ from the narrator’s
                </div>

                <p className="mt-2 text-base leading-7 text-gray-700 sm:text-lg sm:leading-8">
                  Some differences are normal. Compare the{" "}
                  <span className="font-semibold">location and duration</span> of
                  the gaps, not simply how many you have.
                </p>

                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <div className="text-lg font-semibold text-gray-900 sm:text-xl">
                      Possible pronunciation differences
                    </div>
                    <ul className="mt-2 space-y-2 text-base leading-7 text-gray-700 sm:text-lg sm:leading-8">
                      <li>• A brief pause or hesitation inside the phrase</li>
                      <li>• A breath reset where the narrator continues</li>
                      <li>• A longer closure on p, t, or k</li>
                      <li>• Loss of voicing where the narrator remains voiced</li>
                    </ul>
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <div className="text-lg font-semibold text-gray-900 sm:text-xl">
                      Possible tracking failures
                    </div>
                    <ul className="mt-2 space-y-2 text-base leading-7 text-gray-700 sm:text-lg sm:leading-8">
                      <li>• Background noise or a distant microphone</li>
                      <li>• Speech that is very quiet</li>
                      <li>• Irregular, creaky, or breathy vocal-fold vibration</li>
                      <li>• A brief pitch-analysis error</li>
                    </ul>
                  </div>
                </div>

                <p className="mt-4 text-base leading-7 text-gray-700 sm:text-lg sm:leading-8">
                  Replay the relevant word or phrase and compare what you hear
                  with the narrator before treating a visual difference as a
                  pronunciation error.
                </p>
              </section>

              <div className="rounded-xl border border-blue-100 bg-blue-50/50 px-4 py-4 text-base leading-7 text-gray-700 sm:text-lg sm:leading-8">
                <span className="font-semibold text-gray-900">Remember:</span>{" "}
                the blue curve’s{" "}
                <span className="font-semibold text-blue-700">shape</span> shows
                pitch movement. A <span className="font-semibold">gap</span> means
                that no reliable pitch was detected; it may correspond to a
                voiceless sound, a pause, or occasionally a tracking failure.
              </div>

              <div className="text-sm leading-6 text-gray-500 sm:text-base">
                Tip: click words in the visualization to replay them and connect
                what you hear with the graph.
              </div>
            </div>
          </main>

          <footer className="sticky bottom-0 z-20 shrink-0 border-t border-gray-200 bg-white">
            <div className="flex items-center justify-end px-4 py-3 sm:px-6">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 px-5 py-2.5 text-base font-semibold text-white shadow-sm hover:opacity-95"
              >
                Close help
              </button>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}